/**
 * Levels API.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type { Level, CreateLevelDto, UpdateLevelDto } from '@/types/level.types';
import type { PaginatedResponse } from '@/types/data-table.types';

/** Append query params to a path when the search string is non-empty. */
function listUrl(path: string, searchParams: URLSearchParams): string {
  const q = searchParams.toString();
  return q ? `${path}?${q}` : path;
}

/** Trigger a browser download of an XLSX blob. */
function downloadXlsx(data: Blob, filename: string): void {
  const blob = new Blob([data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
}

/** `GET /academic/levels` — paginated levels. */
export const getLevels = async (params?: {
  page?: number;
  pageSize?: number;
  cycles?: string[];
  statuses?: string[];
  search?: string;
  minAge?: number;
  maxAge?: number;
}): Promise<PaginatedResponse<Level>> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  params?.cycles?.forEach((c) => searchParams.append('cycles', c));
  params?.statuses?.forEach((s) => searchParams.append('statuses', s));
  if (params?.search) searchParams.append('search', params.search);
  if (params?.minAge !== undefined) searchParams.append('minAge', params.minAge.toString());
  if (params?.maxAge !== undefined) searchParams.append('maxAge', params.maxAge.toString());
  const { data } = await apiClient.get<PaginatedResponse<Level>>(
    listUrl('/academic/levels', searchParams),
  );
  return data;
};

/** `GET /academic/levels/:id` */
export const getLevel = async (id: string): Promise<Level> => {
  const { data } = await apiClient.get(`/academic/levels/${id}`);
  return data;
};

/** `POST /academic/levels` */
export const createLevel = async (dto: CreateLevelDto): Promise<Level> => {
  const { data } = await apiClient.post('/academic/levels', dto);
  return data;
};

/** `PATCH /academic/levels/:id` */
export const updateLevel = async (id: string, dto: UpdateLevelDto): Promise<Level> => {
  const { data } = await apiClient.patch(`/academic/levels/${id}`, dto);
  return data;
};

/** `DELETE /academic/levels/:id` */
export const deleteLevel = async (id: string): Promise<void> => {
  await apiClient.delete(`/academic/levels/${id}`);
};

/** `PATCH /academic/levels/reorder` */
export const reorderLevels = async (ids: string[]): Promise<void> => {
  await apiClient.patch('/academic/levels/reorder', { ids });
};

/** `GET /academic/levels/export` — download XLSX. */
export const exportLevelsExcel = async (): Promise<void> => {
  const response = await apiClient.get('/academic/levels/export', { responseType: 'blob' });
  downloadXlsx(response.data, `levels-${new Date().toISOString().slice(0, 10)}.xlsx`);
};
